import type { Session } from '@supabase/supabase-js';

const baseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined || 'http://localhost:8000').replace(/\/$/, '');

export type Usage = { total_granted: number; reserved_uses: number; consumed_uses: number; available_uses: number };
export type Job = {
  id: string;
  usage_event_id: string;
  title: string | null;
  word_count: number;
  status: 'queued' | 'processing' | 'retrying' | 'completed' | 'failed' | 'cancelled';
  output_text: string | null;
  error_code: string | null;
  error_message: string | null;
  retry_count: number;
  reservation_active: boolean;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  updated_at: string;
};
export type Package = { id: string; name: string; price_minor: number; currency: string; allowance: number; active: boolean };

async function request<T>(path: string, session: Session | null, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('content-type', 'application/json');
  if (session?.access_token) headers.set('authorization', `Bearer ${session.access_token}`);
  const response = await fetch(`${baseUrl}/api${path}`, { ...init, headers });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = body?.error?.message;
    throw new Error(typeof message === 'string' ? message : 'The request could not be completed.');
  }
  return body as T;
}

export const api = {
  me: (session: Session) => request<{ user: { id: string; email: string | null; role: string; paddle_customer_id: string | null }; usage: Usage }>(`/me`, session),
  packages: () => request<{ items: Package[] }>('/packages', null),
  jobs: (session: Session, page = 1) => request<{ items: Job[]; page: number; page_size: number }>(`/jobs?page=${page}`, session),
  job: (session: Session, id: string) => request<Job>(`/jobs/${encodeURIComponent(id)}`, session),
  humanize: (session: Session, title: string | null, text: string) => request<{ status: 'queued'; request_id: string; event_id: string; job_id: string }>('/humanize', session, { method: 'POST', body: JSON.stringify({ title, text }) }),
  checkout: (session: Session, packageId: string) => request<{ purchase_id: string; checkout_url: string }>('/payments/create', session, { method: 'POST', body: JSON.stringify({ package_id: packageId }) }),
  adminMetrics: (session: Session) => request<Record<string, number>>('/admin/metrics', session),
  adminUsers: (session: Session) => request<{ items: Array<Record<string, unknown>> }>('/admin/users', session),
  adminJobs: (session: Session) => request<{ items: Array<Record<string, unknown>> }>('/admin/jobs', session),
  adminAdjust: (session: Session, userId: string, delta: number) => request<Record<string, unknown>>(`/admin/users/${encodeURIComponent(userId)}/allowance`, session, { method: 'POST', body: JSON.stringify({ delta, reason: 'Admin dashboard adjustment' }) }),
  adminDisable: (session: Session, userId: string, disabled: boolean) => request<Record<string, unknown>>(`/admin/users/${encodeURIComponent(userId)}/disabled`, session, { method: 'PATCH', body: JSON.stringify({ disabled, reason: 'Admin dashboard account control' }) }),
  adminRetry: (session: Session, jobId: string) => request<Record<string, unknown>>(`/admin/jobs/${encodeURIComponent(jobId)}/retry`, session, { method: 'POST', body: '{}' }),
};

export function isApiConfigured() {
  return Boolean(import.meta.env.VITE_API_BASE_URL);
}
